import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CardDataNotFound from "../../components/CardDataNotFound/CardDataNotFound";
import { CustomButtonVariant } from "../../components/CustomButton/CustomButton";
import CollapsibleFilterCard from "../../components/CollapsibleFilterCard/CollapsibleFilterCard";
import CustomCard from "../../components/CustomCard/CustomCard";
import Layout from "../../components/Layout/Layout";
import QueryPageTitleCard from "../../components/QueryPageTitleCard/QueryPageTitleCard";
import StatusTag, { StatusTagType } from "../../components/StatusTag/StatusTag";
import { StyledIcon } from "../../components/StyledIcon/StyledIcon";
import TablePaginated from "../../components/TablePaginated/TablePaginated";
import { useDistributor } from "../../contexts/distributor/DistributorContext";
import { useIocContext } from "../../contexts/ioc/IocContext";
import useDialogAlert from "../../hooks/DialogAlert";
import { Types } from "../../ioc/types";
import { DownloadNfeQuery } from "../../modules/financial/models/DownloadNfeQuery";
import { GetAllNfeQuery } from "../../modules/financial/models/GetAllNfeQuery";
import { IFinancialService } from "../../modules/financial/models/IFinancialService";
import {
  calcPagesQuantity,
  getThousand,
  getTypeTagCancelledNote,
  isCancelled,
} from "../../utils";
import AppError from "../../utils/appError";
import { formatCurrency } from "../../utils/currencyFormatter";
import { convertToDefaultDate } from "../../utils/dateFormatter";
import { downloadExcel, downloadThisWindow } from "../../utils/download";
import InvoicesFilter from "./Sections/InvoicesFilter";
import { DownloadList, DownloadType } from "./models/Download";
import { TableHeaderColumns } from "./models/TableHeaderColumns";
import DetailsModal from "../../components/ModalDetailsGeneric/ModalDetailsGeneric";
import {
  STATUS_NOTE_CANCELLED,
  STATUS_NOTE_SENT,
} from "../../utils/enumStatusNote";
import { TableSummary } from "./TableSummaryData";
import { ISummaryContent } from "../../modules/financial/models/GetAllNfeDTO";

const ROW_PER_PAGE = 10;
interface TableRow {
  id: string;
  noteNumber: string;
  product: string;
  dateOfIssue: string;
  dueDate: string;
  quantity: string;
  price: string;
  accessKey: string;
  isLoadingDownloadNfe?: boolean;
  CFOP: string;
  operationNature: string;
  unitOfMeasure?: string;
  cancelled: React.ReactNode;
  isCancelled: boolean;
  companyName: string;
}

const ConsultInvoicesPage: React.FC = () => {
  const [data, setData] = useState<TableRow[]>([]);
  const [errorData, setErrorData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [total, setTotal] = useState(0);
  const { snackbar } = useDialogAlert();
  const { selectedDistributor } = useDistributor();

  const [filter, setFilter] = useState<GetAllNfeQuery | null>(null);
  const [isClear, setClear] = useState<boolean>(false);
  const storageExternalId = selectedDistributor?.externalId;
  const isClickedInFilterStorage = localStorage.getItem("isClickedInFilter");

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dataModalDetail, setDataModalDetail] = useState<TableRow>();
  const [summary, setSummary] = useState<ISummaryContent | null | undefined>(
    null
  );
  const { serviceContainer } = useIocContext();

  const financialService = serviceContainer.get<IFinancialService>(
    Types.Financial.IFinancialService
  );

  const isView = useMemo(() => {
    return (
      (filter && !errorData && !isClear) || (filter && !errorData && isClear)
    );
  }, [filter, errorData, isClear]);

  const getAllNfe = useCallback(async () => {
    try {
      if (!filter) return;
      setErrorData(false);
      setIsLoadingData(true);
      const response = await financialService.getAllNfe(filter);
      const newData = response.content.map((item, index) => ({
        id: `${index}-${item?.CNPJ}`,
        noteNumber: item?.electronicInvoiceNumber,
        product: item?.materialDescription,
        dateOfIssue: convertToDefaultDate(item?.creationDate),
        dueDate: convertToDefaultDate(item?.dueDate),
        quantity: `${getThousand(item?.quantity ?? 0)} ${item?.unitOfMeasure}`,
        price: formatCurrency(item?.invoiceTotal),
        accessKey: item?.accessKey,
        CFOP: item?.CFOP,
        operationNature: item?.operationNature,
        unitOfMeasure: item?.unitOfMeasure,
        cancelled: (
          <StatusTag
            text={isCancelled(item?.cancelled)}
            type={getTypeTagCancelledNote(item?.cancelled)}
            marginLeft={"10px"}
          />
        ),
        isCancelled: item?.cancelled,
        companyName: item?.companyName,
      }));

      if (response.content.length === 0) {
        setErrorData(true);
        setSummary(null);
      }
      setTotal(response.total);
      setSummary(response.summary);
      setData(newData);
    } catch (error) {
      snackbar({
        message: "Erro ao carregar notas fiscais. Tentar novamente",
        variant: "error",
      });
      setErrorData(true);
    } finally {
      setIsLoadingData(false);
    }
  }, [filter]);

  const fetchDownlaod = async (query: DownloadNfeQuery, index: number) => {
    try {
      const response = await financialService.downloadNfeReport(query);
      switch (query.type) {
        case "pdf":
          downloadThisWindow(response, "pdf", query);
          break;
        case "xml":
          downloadThisWindow(response, "xml", query);
          break;
      }
      snackbar({
        message: "Download feito com sucesso",
        variant: "success",
      });
    } catch (error) {
      if ((error as AppError).statusCode === 404) {
        snackbar({
          message: "Nota fiscal não encontrada",
          variant: "error",
        });
      } else {
        snackbar({
          message: "Erro ao fazer download. Tentar novamente",
          variant: "error",
        });
      }
    }
  };

  const downloadReport = useCallback(async () => {
    try {
      if (!filter) return;
      setIsLoadingDownload(true);
      const response = await financialService.downloadReportNfe({
        service: "NFE",
        ...filter,
      });
      downloadExcel(response);
      snackbar({
        message: "Download feito com sucesso",
        variant: "success",
      });
    } catch (error) {
      snackbar({
        message: "Erro ao fazer download. Tentar novamente",
        variant: "error",
      });
    } finally {
      setIsLoadingDownload(false);
    }
  }, [filter]);

  useEffect(() => {
    if (filter) {
      getAllNfe();
    }
  }, [filter]);

  interface StyledMenuProps {
    index: number;
    accessKey: string;
    item: TableRow;
  }

  const StyledMenu: React.FC<StyledMenuProps> = ({
    index,
    accessKey,
    item,
  }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const updateDate = (isLoading: boolean) => {
      const newData = [...data];
      newData[index].isLoadingDownloadNfe = isLoading;
      setData(newData);
    };

    const handleMenuItemClick = async (type: DownloadType) => {
      setAnchorEl(null);
      switch (type) {
        case "pdf":
        case "xml":
          updateDate(true);
          await fetchDownlaod(
            {
              type,
              codes: [accessKey],
            },
            index
          );
          updateDate(false);
          break;
        case "tax":
          break;
        case "detailNote":
          setDataModalDetail(item);
          setOpenDialog(true);

          break;
        default:
          break;
      }
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <div>
        <IconButton onClick={handleClickListItem}>
          <StyledIcon iconType="three-vertical-points" />
        </IconButton>
        <Menu
          id="lock-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "lock-button",
            role: "listbox",
          }}
          sx={{
            minWidth: 300,
          }}
        >
          {DownloadList.map((option, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                handleMenuItemClick(option.type);
              }}
              sx={{
                minWidth: 200,
                fontSize: 14,
                fontFamily: "Open Sans",
                "&.Mui-selected": {
                  color: "shadesOfDark.dark",
                  backgroundColor: "shadesOfDark.ultraLight",
                  fontWeight: 700,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "shadesOfDark.ultraLight",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  };

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{
              width: 60,
              height: 60,
            }}
          />
        ),
      }}
    >
      <DetailsModal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title="Detalhes da Nota"
        header={{
          title: dataModalDetail?.companyName || "",
        }}
        data={[
          {
            label: "Número da nota",
            value: (
              <Box display="flex" alignItems="center" gap={2}>
                {dataModalDetail?.noteNumber ?? "-"}
                <StatusTag
                  text={
                    dataModalDetail?.isCancelled
                      ? STATUS_NOTE_CANCELLED
                      : STATUS_NOTE_SENT
                  }
                  type={
                    dataModalDetail?.isCancelled
                      ? StatusTagType.LATE
                      : StatusTagType.ON_TIME
                  }
                />
              </Box>
            ),
          },
          { label: "CFOP", value: dataModalDetail?.CFOP },
          { label: "Emissão", value: dataModalDetail?.dateOfIssue },
          { label: "Vencimento", value: dataModalDetail?.dueDate },
          { label: "Valor", value: dataModalDetail?.price },
          { label: "Quantidade", value: dataModalDetail?.quantity },

          {
            label: "Natureza da operação",
            value: dataModalDetail?.operationNature,
            fullWidth: true,
          },
        ]}
        actions={[
          {
            label: "Fechar",
            variant: CustomButtonVariant.CONTAINED,
            onClick: () => setOpenDialog(false),
          },
        ]}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <QueryPageTitleCard title="Notas Fiscais" />
            </Grid>

            <Grid item xs={12}>
              <CollapsibleFilterCard
                title="Consultar Nota Fiscal"
                subtitle="Informe os parâmetros de pesquisa para consultar a nota fiscal. As informações estão disponíveis para um período máximo de 5 anos."
              >
                <InvoicesFilter
                  onButtonChange={(value) => {
                    setFilter(value);
                  }}
                  onButtonClearFilter={(value) => {
                    setClear(value);
                  }}
                  isLoadingDownload={isLoadingDownload}
                  errorData={errorData}
                  onDownloadReport={downloadReport}
                />
              </CollapsibleFilterCard>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomCard
            sx={{
              boxShadow: "none",
              border: "1px solid #DFE0EB",
              height: "100%",
              padding: 2.4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!filter?.id &&
            isClear &&
            !storageExternalId &&
            !isClickedInFilterStorage ? (
              <Box alignSelf="center">
                <CardDataNotFound
                  icon="search-empty"
                  title="Selecione um ou mais produtos para começar"
                  subtitle="Informe outros critérios de busca e comece a consultar!"
                />
              </Box>
            ) : !errorData && isView ? (
              <>
                {!isLoadingData && <TableSummary data={summary} />}
                <TablePaginated
                  columns={TableHeaderColumns}
                  data={data.map((item, index) => ({
                    ...item,
                    icon: item.isLoadingDownloadNfe ? (
                      <CircularProgress size={16} color="secondary" />
                    ) : (
                      <StyledMenu
                        index={index}
                        accessKey={item.accessKey}
                        item={item}
                      />
                    ),
                  }))}
                  rowsPerPage={ROW_PER_PAGE}
                  isLoadingData={isLoadingData}
                  loadFromServer
                  onChangePage={(newPage) => {
                    setFilter((oldState: any) => ({
                      ...oldState,
                      page: newPage,
                    }));
                  }}
                  paginationProps={{
                    pagesTotal: calcPagesQuantity(ROW_PER_PAGE, total),
                    // page: filter && filter.page,
                  }}
                  resultsTotal={total}
                  isStickyHeader
                />
              </>
            ) : (
              <CardDataNotFound
                icon="cactus"
                title="Não foram encontradas notas fiscais"
                subtitle="Informe outros critérios de busca e consulte novamente."
              />
            )}
          </CustomCard>
        </Box>
      </Box>
    </Layout>
  );
};

export default ConsultInvoicesPage;
